import React, {
  MouseEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { RoutePaths, routeGroups } from "../../enumStrings";
import {
  ItemsPurchasableByChamberEnum,
  UserType,
} from "../../graphql/types";
import cmpLogo from "../../images/cmp_logo.png";
import useGetChamberFromUserIdInAuth from "../../hooks/useGetChamberFromUserIdInAuth";
import { useHandleLogout } from "../../hooks/useHandleLogout";
import useGetUserFromUserIdInAuth from "../../hooks/useGetUserFromUserIdInAuth";
import { UserTypeContext } from "../../contexts/UserTypeContext";
import { useCmpOrganizationNoun } from "../../contexts/CmpOrganizationNounContext";
import { applyCmpOrganizationNounToPageName } from "../../utils/cmpOrganizationNoun";
import {
  filterCmpRouteGroups,
  isCmpExternalRoutePermissionLocked,
} from "../../utils/cmpRouteAccess";
import { getCmpExternalRouteHref } from "../../utils/cmpExternalRouteHref";
import { STAFF_SUPPORT_EMAIL } from "../../utils/userFacingError";
import {
  buildCmpNavbarPresentation,
  CMP_NAVBAR_ITEM_IDS,
  CmpNavbarGroup,
  CmpNavbarGroupId,
  CmpNavbarItem,
} from "./cmpNavbarPresentation";
import { NavbarIcon } from "./NavbarIcon";
import CmpWhatsNew from "../pages/WhatsNew/CmpWhatsNew";
import { groupHasUpdate, itemHasUpdate, useCmpWhatsNewNav } from "../pages/WhatsNew/useCmpWhatsNewNav";
import { NEED_HELP, SIDEBAR_UPDATE_STRINGS } from "../pages/UserGuide/designStrings";
import { CmpUserGuideViewerStatusEnum, useGetCmpUserGuideQuery } from "../../graphql/types";
import {
  CMP_USER_GUIDE_CONTENTS_SLUG,
  guideHref,
  guideOperationContext,
  useMarkCmpUserGuideViewed,
} from "../pages/UserGuide/CmpUserGuide";

type NavbarProps = {
  onClose?: () => void;
};

const activeGroupIds = (groups: readonly CmpNavbarGroup[]) =>
  groups
    .filter((group) => group.items.some((item) => item.isActive))
    .map((group) => group.id);

const destinationHref = (item: CmpNavbarItem) =>
  `${item.destination.pathname}${item.destination.search || ""}`;

const isUnmodifiedPrimaryClick = (event: MouseEvent<HTMLAnchorElement>) =>
  !event.defaultPrevented &&
  event.button === 0 &&
  !event.altKey &&
  !event.ctrlKey &&
  !event.metaKey &&
  !event.shiftKey;

// S10 (Moeen Q6 Oct 28 2026): the reason on a greyed Website Builder or
// Chamber Perks App™ Dashboard link names the permission in Employees &
// Permissions (permissionCatalogue.ts labels).
const getPermissionLockedReason = (
  item: CmpNavbarItem,
  organizationNoun: ReturnType<typeof useCmpOrganizationNoun>,
) => {
  const [product, permission] = item.id === CMP_NAVBAR_ITEM_IDS.websiteBuilder
    ? ["the Website Builder", "Access Website Builder"]
    : ["the Chamber Perks App™ Dashboard", "Access Chamber Perks App™ Dashboard"];
  return `You cannot open ${product} because your login does not have the ${permission} permission. Ask your ${organizationNoun.singular} owner to add it in ${applyCmpOrganizationNounToPageName("Chamber Set Up", organizationNoun)} > Employees & Permissions.`;
};

const Navbar: React.FC<NavbarProps> = ({ onClose }) => {
  const location = useLocation();
  const chamberInfo = useGetChamberFromUserIdInAuth();
  const organizationNoun = useCmpOrganizationNoun();
  const loggedInUser = useGetUserFromUserIdInAuth();
  const {
    state: { userType },
  } = useContext(UserTypeContext);
  const handleLogout = useHandleLogout();
  const previousLocationSignatureRef = useRef(
    `${location.pathname}${location.search}`,
  );
  const openMenusLocationSignatureRef = useRef(
    `${location.pathname}${location.search}`,
  );

  const routeAccess = useMemo(
    () => ({
      userType,
      chamberMemberProPermissions:
        loggedInUser?.usagePermissions
          ?.chamberMemberPro_EnabledPages || [],
      chamberPerksDashboardPermissions:
        loggedInUser?.usagePermissions
          ?.chamberPerksAppAdminConsole_EnabledPages || [],
      hasWebsiteBuilderEntitlement: Boolean(
        chamberInfo?.purchasedItems?.includes(
          ItemsPurchasableByChamberEnum.ChamberMemberProWebsiteBuilderOnly,
        ),
      ),
      hasChamberPerksAppEntitlement: Boolean(
        chamberInfo?.purchasedItems?.includes(
          ItemsPurchasableByChamberEnum.ChamberPerksApp,
        ),
      ),
    }),
    [chamberInfo?.purchasedItems, loggedInUser?.usagePermissions, userType],
  );

  const allowedRouteGroups = useMemo(
    () =>
      filterCmpRouteGroups({
        routeGroups: routeGroups[UserType.ChamberAdmin],
        access: routeAccess,
      }),
    [routeAccess],
  );

  const permissionLockedRoutes = useMemo(
    () =>
      routeGroups[UserType.ChamberAdmin]
        .flatMap((routeGroup) =>
          routeGroup.nestedRoute
            ? routeGroup.nestedRoute.routes
            : routeGroup.route
              ? [routeGroup.route]
              : [],
        )
        .filter((route) =>
          isCmpExternalRoutePermissionLocked({ route, access: routeAccess }),
        ),
    [routeAccess],
  );

  const allowedRoutes = useMemo(
    () =>
      allowedRouteGroups.flatMap((routeGroup) =>
        routeGroup.nestedRoute
          ? routeGroup.nestedRoute.routes
          : routeGroup.route
            ? [routeGroup.route]
            : [],
      ),
    [allowedRouteGroups],
  );

  const navbarPresentation = useMemo(
    () =>
      buildCmpNavbarPresentation({
        allowedRoutes,
        permissionLockedRoutes,
        chamberName: chamberInfo?.name,
        pathname: location.pathname,
        search: location.search,
      }),
    [allowedRoutes, permissionLockedRoutes, chamberInfo?.name, location.pathname, location.search],
  );

  // CMP User Guide (Monday 13280799093): "New" until first opened,
  // "Updated" after a change the user has not seen.
  const userGuide = useGetCmpUserGuideQuery({
    fetchPolicy: "cache-and-network",
    context: guideOperationContext,
  }).data?.getCmpUserGuide;
  const isGuidePage = location.pathname === RoutePaths.chamberAdmin_guide;
  const markGuideViewed = useMarkCmpUserGuideViewed();
  const guidePill =
    userGuide?.viewerStatus === CmpUserGuideViewerStatusEnum.New
      ? "New"
      : userGuide?.viewerStatus === CmpUserGuideViewerStatusEnum.Updated
        ? "Updated"
        : null;

  const [openMenus, setOpenMenus] = useState<CmpNavbarGroupId[]>(() =>
    activeGroupIds(navbarPresentation.mainGroups),
  );
  const didInitializeOpenMenusRef = useRef(false);
  const locationSignature = `${location.pathname}${location.search}`;

  // What's New + Updated pills (Monday 13282310441, Claude Design (19) navUpdates()).
  const activeItem = useMemo(
    () => navbarPresentation.mainGroups.flatMap((group) => group.items).find((item) => item.isActive) || null,
    [navbarPresentation.mainGroups],
  );
  const whatsNew = useCmpWhatsNewNav({ activeItem, locationSignature });

  useEffect(() => {
    const previousLocationSignature = previousLocationSignatureRef.current;
    previousLocationSignatureRef.current = locationSignature;

    if (previousLocationSignature !== locationSignature) {
      onClose?.();
    }
  }, [locationSignature, onClose]);

  useEffect(() => {
    const didLocationChange =
      openMenusLocationSignatureRef.current !== locationSignature;
    openMenusLocationSignatureRef.current = locationSignature;
    const currentActiveGroupIds = activeGroupIds(
      navbarPresentation.mainGroups,
    );

    if (!didInitializeOpenMenusRef.current) {
      if (navbarPresentation.mainGroups.length === 0) {
        return;
      }

      didInitializeOpenMenusRef.current = true;
      setOpenMenus(currentActiveGroupIds);
      return;
    }

    if (!didLocationChange) {
      return;
    }

    setOpenMenus((currentOpenMenus) => {
      const nextOpenMenus = Array.from(
        new Set([...currentOpenMenus, ...currentActiveGroupIds]),
      );

      return nextOpenMenus.length === currentOpenMenus.length
        ? currentOpenMenus
        : nextOpenMenus;
    });
  }, [locationSignature, navbarPresentation.mainGroups]);

  const handleToggleMenu = (groupId: CmpNavbarGroupId) => {
    setOpenMenus((currentOpenMenus) =>
      currentOpenMenus.includes(groupId)
        ? currentOpenMenus.filter((menuId) => menuId !== groupId)
        : [...currentOpenMenus, groupId],
    );
  };

  const handleInternalLinkClick = (
    event: MouseEvent<HTMLAnchorElement>,
    item: CmpNavbarItem,
  ) => {
    if (!onClose || !isUnmodifiedPrimaryClick(event)) {
      return;
    }

    if (destinationHref(item) === locationSignature) {
      onClose();
    }
  };

  const renderItem = ({
    item,
    showIcon,
  }: {
    item: CmpNavbarItem;
    showIcon: boolean;
  }) => {
    const sharedClassName = showIcon
      ? `flex w-full items-center gap-[10px] bg-transparent px-[12px] py-[12px] text-left text-[14px] no-underline transition-colors hover:text-cpaGold focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cpaGold ${
          item.isActive ? "font-bold text-cpaGold" : "font-normal text-white"
        }`
      : `relative flex w-full items-center bg-transparent py-[9px] pl-[40px] pr-[12px] text-left text-[14px] no-underline transition-colors hover:text-cpaGold focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cpaGold ${
          item.isActive ? "font-bold text-cpaGold" : "font-normal text-white"
        }`;
    const content = (
      <>
        {showIcon && item.icon && (
          <NavbarIcon
            className="h-[20px] w-[20px] shrink-0 object-contain"
            isActive={item.isActive}
            name={item.icon}
          />
        )}
        {/* Design (19) sub items: the name, then its Coming Soon pill / update dot right after it. */}
        <span className={showIcon ? "min-w-0 flex-1" : "min-w-0"}>
          {applyCmpOrganizationNounToPageName(item.label, organizationNoun)}
        </span>
        {item.badge && (
          <span className="ml-[8px] shrink-0 whitespace-nowrap rounded-full bg-cpaGold px-[7px] py-[3px] text-[10px] font-bold leading-none text-[#0A0A0A]">
            {item.badge}
          </span>
        )}
        {itemHasUpdate(item, whatsNew.updated) && item.updateMarker === "new" && (
          <span
            className="ml-[8px] shrink-0 rounded-full bg-[#F21B1B] px-[7px] py-[3px] text-[10px] font-bold leading-none text-white"
            data-testid={`cmp-nav-new-${item.whatsNewKey}`}
          >
            {SIDEBAR_UPDATE_STRINGS.newPill}
          </span>
        )}
        {itemHasUpdate(item, whatsNew.updated) && item.updateMarker === "dot" && (
          <span
            className="ml-[8px] h-[6px] w-[6px] shrink-0 rounded-full bg-[#F21B1B]"
            data-testid={`cmp-nav-dot-${item.whatsNewKey}`}
            title={SIDEBAR_UPDATE_STRINGS.dotTitle}
          />
        )}
        {!showIcon && item.isActive && (
          <span
            aria-hidden="true"
            className="absolute inset-y-0 right-0 w-[4px] rounded-[2px] bg-cpaGold"
          />
        )}
      </>
    );

    if (item.permissionLocked) {
      return (
        <span
          key={item.id}
          aria-disabled="true"
          className={`${sharedClassName} cursor-not-allowed opacity-60`}
          data-testid={`cmp-nav-locked-${item.id}`}
          title={getPermissionLockedReason(item, organizationNoun)}
        >
          {content}
        </span>
      );
    }

    if (item.route.isExternalLink) {
      const externalHref = getCmpExternalRouteHref({
        destination: item.route.externalLinkDestination,
        routePath: item.route.path,
        websiteUrl: chamberInfo?.chamberMemberProWebsiteUrl,
      });

      if (!externalHref) {
        return (
          <span
            key={item.id}
            aria-disabled="true"
            className={`${sharedClassName} cursor-not-allowed opacity-60`}
            title={`Website Builder cannot open because your ${organizationNoun.singular} website address is not set up. To set it up, email ${STAFF_SUPPORT_EMAIL}.`}
          >
            {content}
          </span>
        );
      }

      return (
        <a
          key={item.id}
          className={sharedClassName}
          href={externalHref}
          onClick={(event) => {
            if (onClose && isUnmodifiedPrimaryClick(event)) {
              onClose();
            }
          }}
          rel="noopener noreferrer"
          target="_blank"
        >
          {content}
        </a>
      );
    }

    return (
      <Link
        key={item.id}
        aria-current={item.isActive ? "page" : undefined}
        className={sharedClassName}
        onClick={(event) => {
          if (isUnmodifiedPrimaryClick(event) && whatsNew.onItemClick(item)) {
            event.preventDefault();
            onClose?.();
            return;
          }
          handleInternalLinkClick(event, item);
        }}
        to={{
          pathname: item.destination.pathname,
          search: item.destination.search,
        }}
      >
        {content}
      </Link>
    );
  };

  return (
    <nav
      aria-label="Primary navigation"
      // Claude Design (20) sidebar (NAV-S1): the logo stays at the top, only
      // the menu list scrolls, and Need Help? and Logout stay pinned at the
      // bottom. On a very short window (480 px or less) the whole sidebar
      // scrolls instead, so the pinned part never hides the menu.
      className="relative flex h-full min-h-0 w-[256px] max-w-[85vw] flex-col overflow-hidden bg-[#0A0A0A] pt-[20px] text-white [@media(max-height:480px)]:overflow-y-auto [@media(max-height:480px)]:overscroll-contain"
      style={{
        fontFamily: '"DM Sans", Arial, sans-serif',
        lineHeight: "normal",
      }}
    >
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute right-[8px] top-[8px] z-10 flex h-[40px] w-[40px] items-center justify-center rounded-[2px] text-white transition-colors hover:text-cpaGold focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cpaGold md:hidden"
          aria-label="Close navigation"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      )}

      <div className="shrink-0 px-[20px] pb-[16px]">
        <img
          src={cmpLogo}
          alt="Chamber Member Pro"
          className="mb-[6px] mt-[2px] h-[46px] max-w-full object-contain object-left"
        />
        <p className="mt-[6px] text-[11px] font-normal text-white">
          Part of One Chamber System™
        </p>
        <div className="mt-[14px] h-px bg-[#262626]" />
      </div>

      <div
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain [@media(max-height:480px)]:flex-none [@media(max-height:480px)]:overflow-visible"
        data-testid="cmp-nav-menu-scroll"
      >
      {/* Claude Design (17): one "Chamber Member Pro™ / User Guide" item that
          opens Guide Contents; the guide pages are listed there, not here. */}
      <div className="shrink-0" data-testid="cmp-nav-guide">
        <Link
          aria-current={isGuidePage ? "page" : undefined}
          className={`flex w-full items-center gap-[10px] bg-transparent px-[12px] py-[12px] text-left text-[14px] font-bold no-underline transition-colors hover:text-cpaGold focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cpaGold ${
            isGuidePage ? "text-cpaGold" : "text-white"
          }`}
          onClick={() => {
            markGuideViewed();
            if (onClose) onClose();
          }}
          to={guideHref(CMP_USER_GUIDE_CONTENTS_SLUG)}
        >
          <NavbarIcon
            className="h-[20px] w-[20px] shrink-0"
            color={isGuidePage ? "#E1A731" : "#FFFFFF"}
            name="book-open"
          />
          <span className="flex min-w-0 flex-1 flex-col gap-[2px]">
            <span className="whitespace-nowrap leading-[1.3]">Chamber Member Pro™</span>
            <span className="flex items-center gap-[8px] whitespace-nowrap leading-[1.3]">
              <span>User Guide</span>
              {guidePill && (
                <span
                  className="shrink-0 rounded-full bg-[#F21B1B] px-[7px] py-[3px] text-[10px] font-bold leading-none tracking-[.02em] text-white"
                  data-testid="cmp-guide-pill"
                >
                  {guidePill}
                </span>
              )}
            </span>
          </span>
        </Link>
      </div>

      <div className="shrink-0">
        {navbarPresentation.mainGroups.map((group) => {
          const isOpen = openMenus.includes(group.id);
          const isActive = group.items.some((item) => item.isActive);
          const isGold = isOpen || isActive;
          const contentId = `navbar-group-${group.id}`;

          return (
            <div key={group.id}>
              <button
                type="button"
                aria-controls={contentId}
                aria-expanded={isOpen}
                className={`flex w-full items-center gap-[10px] bg-transparent px-[12px] py-[12px] text-left text-[14px] font-bold transition-colors hover:text-cpaGold focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cpaGold ${
                  isGold ? "text-cpaGold" : "text-white"
                }`}
                onClick={() => handleToggleMenu(group.id)}
              >
                <NavbarIcon
                  className="h-[20px] w-[20px] shrink-0 object-contain"
                  isActive={isGold}
                  name={group.icon}
                />
                <span className="min-w-0 flex-1">{group.label}</span>
                {groupHasUpdate(group, whatsNew.updated) && (
                  <span
                    className="shrink-0 whitespace-nowrap rounded-full bg-[#F21B1B] px-[7px] py-[3px] text-[10px] font-bold leading-none text-white"
                    data-testid={`cmp-nav-updated-${group.id}`}
                    title={SIDEBAR_UPDATE_STRINGS.moduleTitle}
                  >
                    {SIDEBAR_UPDATE_STRINGS.updated}
                  </span>
                )}
                <span
                  aria-hidden="true"
                  className={`inline-flex shrink-0 transition-transform duration-[120ms] motion-reduce:transition-none ${
                    isOpen ? "rotate-0" : "-rotate-90"
                  }`}
                >
                  <NavbarIcon
                    color={isGold ? "#E1A731" : "#FFFFFF"}
                    name="chevron-down"
                    size={17}
                  />
                </span>
              </button>
              <div id={contentId} hidden={!isOpen}>
                {group.items.map((item) =>
                  renderItem({ item, showIcon: false }),
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="shrink-0">
        {navbarPresentation.footerSections.map((section) => (
          <div key={section.id}>
            {section.dividerBefore && (
              <div className="mx-[20px] my-[8px] h-px bg-[#262626]" />
            )}
            {section.items.map((item) =>
              renderItem({ item, showIcon: true }),
            )}
          </div>
        ))}
      </div>
      </div>

      <div className="shrink-0 pb-[12px]" data-testid="cmp-nav-pinned-footer">
        {/* Claude Design (19) lines 167-174: "Need Help?" opens a Freshdesk ticket (replaces Contact Support). */}
        <div className="mx-[20px] my-[8px] h-px bg-[#262626]" />
        <a
          className="flex w-full items-start gap-[10px] px-[12px] py-[12px] text-[14px] font-normal text-white no-underline hover:text-white hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cpaGold"
          data-testid="cmp-nav-need-help"
          href={NEED_HELP.href}
          onClick={(event) => {
            if (onClose && isUnmodifiedPrimaryClick(event)) onClose();
          }}
          rel="noopener noreferrer"
          target="_blank"
        >
          <NavbarIcon className="h-[20px] w-[20px] shrink-0" color="#FFFFFF" name="help-circle" />
          <span className="flex min-w-0 flex-1 flex-col gap-[2px]">
            <span>{NEED_HELP.title}</span>
            <span className="text-[12px] leading-[1.35] text-[#BDBDBD]">{NEED_HELP.subtitle}</span>
          </span>
        </a>
        <button
          type="button"
          className="flex w-full items-center gap-[10px] bg-transparent px-[12px] py-[12px] text-left text-[14px] font-bold text-[#E24C4C] transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cpaGold"
          onClick={handleLogout}
        >
          <NavbarIcon color="currentColor" name="log-out" />
          <span className="min-w-0 flex-1">Logout</span>
        </button>
      </div>
      {whatsNew.openKey && <CmpWhatsNew onBack={whatsNew.close} pageKey={whatsNew.openKey} />}
    </nav>
  );
};

export default Navbar;
